import React from 'react';

import { Center, Radio, Skeleton, Stack, Table, Text, useMantineTheme } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { ExternalPhoneNumberDto } from 'shared/interfaces/Telephony';

interface PhoneNumberTableProps {
  externalPhoneNumbers: ExternalPhoneNumberDto[];
  selectedNumber: string;
  onSelectNumber: (number: string) => void;
  isLoading: boolean;
}

export function ExternalPhoneNumberTable(phoneNumberTableProps: PhoneNumberTableProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { externalPhoneNumbers, selectedNumber, onSelectNumber, isLoading } = phoneNumberTableProps;
  // We could change this in the future if we decide to add phone numbers are add-ons
  const showFreePricing = true;

  const renderLoadingRows = () =>
    Array.from({ length: 8 }).map((_, index) => (
      <tr key={index}>
        <td>
          <Skeleton height={20} radius="sm" />
        </td>
        <td>
          <Skeleton height={20} radius="sm" />
        </td>
        <td>
          <Skeleton height={20} radius="sm" />
        </td>
        <td>
          <Center>
            <Skeleton height={20} radius="sm" circle />
          </Center>
        </td>
      </tr>
    ));

  const renderNoPhoneNumbersFound = () => (
    <tr style={{ height: '250px' }}>
      <td colSpan={4}>
        <Center>
          <Stack align="center" spacing="xs">
            <Text size="sm" weight={500}>
              {t('SMART_ASSISTANT.TELEPHONY_SECTION.PURCHASE_MODAL.NO_NUMBERS_FOUND')}
            </Text>
            <Text size="xs" color="dimmed">
              {t('SMART_ASSISTANT.TELEPHONY_SECTION.PURCHASE_MODAL.CHANGE_SEARCH_QUERY')}
            </Text>
          </Stack>
        </Center>
      </td>
    </tr>
  );

  const formatPrice = (price: number, currency: string) => {
    return showFreePricing ? t('APP.FREE') : currency === 'USD' ? `$${price}` : `${price} ${currency}`;
  };

  const renderPhoneNumberRows = () =>
    externalPhoneNumbers.map((externalPhoneNumber, index) => (
      <tr
        key={index}
        onClick={() => onSelectNumber(externalPhoneNumber.phoneNumber)}
        style={{
          cursor: 'pointer',
          backgroundColor: selectedNumber === externalPhoneNumber.phoneNumber ? theme.colors.gray[1] : 'transparent'
        }}>
        <td>{externalPhoneNumber.formattedPhoneNumber}</td>
        <td>{externalPhoneNumber.region}</td>
        <td>{formatPrice(externalPhoneNumber.monthlyPrice, externalPhoneNumber.currency)}</td>
        <td>
          <Center>
            <Radio
              value={externalPhoneNumber.phoneNumber}
              checked={selectedNumber === externalPhoneNumber.phoneNumber}
              onChange={() => onSelectNumber(externalPhoneNumber.phoneNumber)}
              onClick={(e) => e.stopPropagation()}
              styles={{ radio: { cursor: 'pointer' } }}
            />
          </Center>
        </td>
      </tr>
    ));

  return (
    <Table>
      <thead>
        <tr>
          <th>{t('SMART_ASSISTANT.TELEPHONY_SECTION.PURCHASE_MODAL.TABLE.PHONE_NUMBER')}</th>
          <th>{t('SMART_ASSISTANT.TELEPHONY_SECTION.PURCHASE_MODAL.TABLE.REGION')}</th>
          <th>{t('SMART_ASSISTANT.TELEPHONY_SECTION.PURCHASE_MODAL.TABLE.PRICE')}</th>
          <th>
            <Center>{t('SMART_ASSISTANT.TELEPHONY_SECTION.TABLE.SELECT')}</Center>
          </th>
        </tr>
      </thead>

      <tbody>
        {isLoading
          ? renderLoadingRows()
          : externalPhoneNumbers.length > 0
          ? renderPhoneNumberRows()
          : renderNoPhoneNumbersFound()}
      </tbody>
    </Table>
  );
}
